import 'chartjs-adapter-date-fns'
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  ChartData,
  ChartOptions,
  Legend,
  LinearScale,
  TimeScale,
  Title,
  Tooltip,
} from 'chart.js'
import { useCallback, useMemo, useRef, useState } from 'react'
import { Bar, getElementsAtEvent } from 'react-chartjs-2'

import { cn } from '@sparkai/utils'

import { type ChartValueClickEventProps, MergedBarLineChartProps, BarChartPluginsType } from '../Charts.types'
import {
  CURRENT_DAY_MERGED_BAR_LINE_OPTIONS,
  DAILY_24H_BAR_AND_LINE_OPTIONS,
  DAILY_BAR_AND_LINE_OPTIONS,
  deepMergeChartOptions,
  DEFAULT_BAR_AND_LINE_OPTIONS,
  WEEKLY_BAR_AND_LINE_OPTIONS,
} from '../configs/options'
import { LABEL_WITH_IMAGE_PLUGIN } from '../configs/plugins'

ChartJS.register(CategoryScale, LinearScale, TimeScale, BarElement, Title, Tooltip, Legend)

function determineChartType(option: string) {
  switch (option) {
    case 'daily':
      return DAILY_BAR_AND_LINE_OPTIONS
    case 'daily-24h':
      return DAILY_24H_BAR_AND_LINE_OPTIONS
    case 'weekly':
      return WEEKLY_BAR_AND_LINE_OPTIONS()
    case 'current-day-engagements':
      return CURRENT_DAY_MERGED_BAR_LINE_OPTIONS
    default:
      return DEFAULT_BAR_AND_LINE_OPTIONS
  }
}

function addPlugins(plugins: Array<BarChartPluginsType> | undefined) {
  return plugins?.includes('label-with-image') ? [LABEL_WITH_IMAGE_PLUGIN] : []
}

export function MergedBarLineChart(props: MergedBarLineChartProps) {
  return <MergedBarChart {...props} />
}

export function MergedBarChart({
  bar,
  line,
  className = '',
  onClick,
  option = 'default',
  optionOverride,
  customTooltip = null,
  onValueClick,
  plugins,
}: Readonly<MergedBarLineChartProps>) {
  const labels = bar.labels
  const classNames = className
  const chartRef = useRef()
  const bD = bar.datasets.map((d) => ({ ...d, yAxisID: 'leftYAxis', type: 'bar' }))
  const lD = line.datasets.map((d) => ({ ...d, yAxisID: 'rightYAxis', type: 'line' }))
  const _data = { labels, datasets: [...lD, ...bD] } as unknown as ChartData<'bar'>
  const _options = useMemo(() => {
    const selectedOption = {
      ...determineChartType(option),
      onClick: (event: ChartValueClickEventProps, ctx?: { index?: number }[]) => {
        if (ctx && ctx.length > 0 && ctx[0].index !== undefined) {
          const label = event.chart.config.data.labels[ctx[0].index]
          onValueClick?.(label)
        }
      },
    }

    return { ...selectedOption } as unknown as ChartOptions<'bar'>
  }, [onValueClick, option])

  const referencePlugins = addPlugins(plugins)

  const [showCustom, setShowCustom] = useState(false)

  if (customTooltip) {
    const base = _options?.plugins ?? {}
    _options.plugins = { ...base, tooltip: { enabled: false } }
  }

  const toggleCustomTooltip = useCallback(
    () => customTooltip && setShowCustom(!showCustom),
    [customTooltip, showCustom]
  )

  const clickHandler = useCallback(
    (event: React.MouseEvent<HTMLCanvasElement, MouseEvent>) => {
      if (chartRef?.current && onClick) {
        onClick(getElementsAtEvent(chartRef?.current, event))
      }
    },
    [onClick]
  )

  const finalOptions = deepMergeChartOptions<'bar'>(_options, optionOverride)

  return (
    <>
      <Bar
        className={cn('h-[232px] max-h-[232px]', classNames, {
          'cursor-pointer': !!onClick,
          relative: customTooltip,
        })}
        ref={chartRef}
        /* eslint-disable-next-line @typescript-eslint/ban-ts-comment */
        // @ts-ignore
        options={finalOptions as unknown as ChartOptions<'bar'>}
        data={_data}
        onClick={clickHandler}
        onMouseEnter={toggleCustomTooltip}
        onMouseLeave={toggleCustomTooltip}
        plugins={referencePlugins}
      />
      {customTooltip && showCustom && customTooltip}
    </>
  )
}
